/** Small shared presentational pieces used across Hub pages. */
import { useEffect, useState } from 'react';
import { divisionLabel, score, statusLabel, tier, typeLabel, DIVISIONS, type DivisionId, type Settings } from './config';
import type { Req } from './lib/data';

export function useHashRoute(): [string, (to: string) => void] {
  const read = () => window.location.hash.replace(/^#/, '') || '/';
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return [route, (to: string) => { window.location.hash = to; }];
}

export function TierBadge({ r, settings }: { r: Req; settings?: Settings }) {
  const s = score(r.impact, r.urgency);
  const t = tier(s, settings?.tiers);
  return (
    <span className={`tier tier-${t}`} title={s == null ? 'Not scored yet' : `Impact ${r.impact} x Urgency ${r.urgency}`}>
      {s == null ? 'unscored' : `${s} ${t}`}
    </span>
  );
}

export function StatusTag({ status }: { status: string }) {
  return <span className={`tag st st-${status}`}>{statusLabel(status)}</span>;
}

export function Divisions({ ids }: { ids: string[] }) {
  return <>{ids.map((d) => <span key={d} className={`tag div div-${d}`}>{divisionLabel(d)}</span>)}</>;
}

export function TypeTag({ type }: { type: string }) {
  return <span className="tag type">{typeLabel(type)}</span>;
}

/** Multi-select division chips; 'all' (Company-wide) is exclusive. */
export function DivisionPicker({ value, onChange, allowAll = true }: {
  value: DivisionId[]; onChange: (v: DivisionId[]) => void; allowAll?: boolean;
}) {
  const toggle = (id: DivisionId) => {
    if (id === 'all') return onChange(value.includes('all') ? [] : ['all']);
    const next = value.filter((v) => v !== 'all');
    onChange(next.includes(id) ? next.filter((v) => v !== id) : [...next, id]);
  };
  return (
    <div className="chips">
      {DIVISIONS.filter((d) => allowAll || d.id !== 'all').map((d) => (
        <button type="button" key={d.id} className={`chip-toggle ${value.includes(d.id) ? 'on' : ''}`} onClick={() => toggle(d.id)}>
          {d.label}
        </button>
      ))}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="empty">{children}</p>;
}
